import { useEffect } from "react";
import { Link } from "react-router-dom";

const EMAIL = "hello@money-muscle.co.uk";

const MailTo = ({ children }) => (
    <a
        href={`mailto:${EMAIL}`}
        className="text-[#C6FF00] transition-colors duration-300 hover:underline"
    >
        {children}
    </a>
);

const H2 = ({ children }) => (
    <h2 className="font-display mt-12 text-xl font-extrabold text-white md:mt-16 md:text-2xl">
        {children}
    </h2>
);

const P = ({ children }) => (
    <p className="mt-4 leading-relaxed text-[#8A8A93]">{children}</p>
);

const UL = ({ items }) => (
    <ul className="mt-4 list-disc space-y-2 pl-5 leading-relaxed text-[#8A8A93]">
        {items.map((item, i) => (
            <li key={i}>{item}</li>
        ))}
    </ul>
);

export default function PrivacyPage() {
    useEffect(() => {
        document.title = "Privacy Policy — MoneyMuscle";
    }, []);

    return (
        <div
            className="min-h-screen bg-[#0A0A0F] px-5 pb-24 pt-10 md:pt-14"
            data-testid="privacy-page"
        >
            <div className="mx-auto max-w-[720px]">
                <Link
                    to="/"
                    data-testid="back-to-home-link"
                    className="text-sm font-semibold text-[#C6FF00] transition-colors duration-300 hover:underline"
                >
                    ← Back to home
                </Link>

                <h1 className="font-display mt-8 text-4xl font-black tracking-[-0.02em] text-white md:text-5xl">
                    Privacy Policy
                </h1>
                <p className="mt-3 text-xs font-bold tracking-[0.25em] text-[#C6FF00]">
                    Effective date: 9 October 2026
                </p>
                <p className="mt-2 text-sm text-[#8A8A93]">
                    Contact: <MailTo>hello@money-muscle.co.uk</MailTo>
                </p>

                <P>
                    MoneyMuscle ("the app", "we") helps you follow a bespoke
                    workout, meal and budget plan. This policy explains what we
                    collect, why, and the choices you have.
                </P>

                <H2>1. What we collect</H2>
                <P>Only what you give us:</P>
                <UL
                    items={[
                        <>
                            Account info: email address, name, hashed password.
                        </>,
                        <>
                            Questionnaire answers: date of birth, sex, height,
                            weight, activity level, fitness goal, diet
                            preference, allergies, cheat-day preferences, pay
                            cycle, monthly income, fixed bills, monthly savings
                            target.
                        </>,
                        <>
                            Logged data: workouts, meals, calories, expenses,
                            weight entries, streaks.
                        </>,
                        <>
                            Progress photos that you choose to save, stored in
                            private object storage.
                        </>,
                        <>
                            Device information limited to what Expo / React
                            Native expose for app operation (OS version, app
                            version).
                        </>,
                    ]}
                />
                <P>
                    We do not collect your location, contacts, calendar, SMS,
                    or background sensor data. We do not use advertising
                    identifiers.
                </P>

                <H2>2. How we use it</H2>
                <UL
                    items={[
                        <>To generate and update your personalised plan.</>,
                        <>To display your trackers, streaks and progress charts.</>,
                        <>
                            To authenticate you and keep your data separate from
                            other users.
                        </>,
                        <>To fix bugs when you report them.</>,
                    ]}
                />

                <H2>3. AI processing</H2>
                <P>
                    Plan generation sends a limited slice of your questionnaire
                    (never your email, name or password) to large language model
                    providers (currently Anthropic Claude) to produce workout
                    and meal suggestions. Providers are contractually bound not
                    to train on this data. If AI providers are unavailable,
                    MoneyMuscle falls back to a deterministic plan computed
                    locally.
                </P>

                <H2>4. Who we share it with</H2>
                <P>No one, except processors acting on our instructions:</P>
                <UL
                    items={[
                        <>
                            Our cloud database host (MongoDB Atlas) to store the
                            data you create.
                        </>,
                        <>
                            Our hosting provider (Emergent) to run the backend.
                        </>,
                        <>AI providers (as described above).</>,
                    ]}
                />
                <P>
                    We do not sell your data. We do not share it with
                    advertisers. We do not share it with insurance companies,
                    employers, lenders, or banks.
                </P>

                <H2>5. Security</H2>
                <UL
                    items={[
                        <>
                            All traffic between the app and our servers is
                            encrypted in transit (HTTPS/TLS).
                        </>,
                        <>Passwords are stored only as salted hashes.</>,
                        <>
                            Progress photos are stored in private object storage
                            requiring a signed URL.
                        </>,
                        <>
                            Access to production data is restricted to the
                            author of the app.
                        </>,
                    ]}
                />

                <H2>6. Your choices</H2>
                <UL
                    items={[
                        <>
                            Edit: change any questionnaire answer at any time
                            from Profile → Goals &amp; Questionnaire.
                        </>,
                        <>
                            Export / delete: email{" "}
                            <MailTo>hello@money-muscle.co.uk</MailTo> from the
                            address on your account. We will delete your account
                            and all associated data within 30 days.
                        </>,
                        <>
                            Opt out of AI processing: email the address above;
                            we'll switch your account to deterministic plans
                            only.
                        </>,
                    ]}
                />

                <H2>7. Children</H2>
                <P>
                    MoneyMuscle is not directed to children under 18. Do not
                    create an account if you are under 18.
                </P>

                <H2>8. International transfers</H2>
                <P>
                    Data may be processed in the United Kingdom, the United
                    States, and the European Union. All processors are bound by
                    standard contractual clauses.
                </P>

                <H2>9. Changes</H2>
                <P>
                    Material changes to this policy will be announced in the app
                    and emailed to the address on your account. The latest
                    version always lives at the URL above.
                </P>

                <H2>10. Contact</H2>
                <P>
                    <MailTo>Hello@money-muscle.co.uk</MailTo>
                </P>
            </div>
        </div>
    );
}
